import { Autocomplete, TmdxTextarea } from '@ds';
import { AdminFormRow, AdminFormSection } from '@ui';
import { Key, memo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { BannedTailNumberFormType } from '@/api/apis/resources';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';
import { useInfinitePartnerProvidersListQuery } from '@/queries/partner-providers';
import { isFieldRequired, renderAutocompleteItem } from '@/utils/helpers';
import { bannedTailNumbersFormSchema } from './config';
import { MAX_LENGTH_2000 } from '@/constants/form.constants';
import { ControlledTextInput } from '../components/inputs/ControlledTextInput';

type BannedTailNumbersProps = {
  isReadOnly?: boolean;
};

export const BannedTailNumbersForm = memo(({ isReadOnly = false }: BannedTailNumbersProps) => {
  const { control, setValue } = useFormContext<BannedTailNumberFormType>();
  const logisticProviderName = useWatch({ control, name: 'logisticProviderName' });

  const {
    searchValue: partnerProviderSearchValue,
    list: partnerProviderList,
    isLoading: isPartnerLoading,
    onSearchValue: onPartnerSearch,
    setAutocompleteOpen: setPartnerAutocompleteOpen,
    itemsScrollerRef: partnerScrollerRef,
    onItemSelectChange: onPartnerSelect,
  } = useInfiniteDropdown({
    setValue: (value: Key | null) => {
      setValue('logisticProviderId', value?.toString() ?? '');
      const partnerProvider = partnerProviderList.find((m) => m.value === value?.toString());
      if (partnerProvider) {
        setValue('logisticProviderName', partnerProvider.name);
      } else {
        setValue('logisticProviderName', '');
      }
    },
    listQuery: useInfinitePartnerProvidersListQuery,
    initialName: logisticProviderName,
    defaultSortOrder: { orderBy: 'name', asc: true },
  });

  return (
    <AdminFormSection title="Tail details">
      <AdminFormRow
        title="Tail Number"
        isRequired={isFieldRequired(bannedTailNumbersFormSchema, 'tailNumber')}
        gap="md"
      >
        <ControlledTextInput name="tailNumber" aria-label="Banned Tail Number" isDisabled={isReadOnly} />
      </AdminFormRow>
      <AdminFormRow title="Logistics provider" gap="md">
        <Controller
          name="logisticProviderId"
          control={control}
          render={({ field: { value }, fieldState: { invalid, error } }) => (
            <Autocomplete
              allowsCustomValue={false}
              placeholder="Partner name"
              aria-label="Logistic provider partner name"
              inputValue={partnerProviderSearchValue}
              scrollRef={partnerScrollerRef}
              items={partnerProviderList}
              isLoading={isPartnerLoading}
              onInputChange={onPartnerSearch}
              isInvalid={invalid}
              isDisabled={isReadOnly}
              errorMessage={error?.message}
              onOpenChange={setPartnerAutocompleteOpen}
              selectedKey={value || null}
              onSelectionChange={onPartnerSelect}
            >
              {renderAutocompleteItem}
            </Autocomplete>
          )}
        />
      </AdminFormRow>
      <AdminFormRow title="Comment" center={false} gap="md">
        <Controller
          name="comment"
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <TmdxTextarea
              fullWidth
              disableAnimation
              minRows={2}
              placeholder="Add comment"
              isInvalid={invalid}
              errorMessage={error?.message}
              className="min-h-16"
              maxLength={MAX_LENGTH_2000}
              {...field}
            />
          )}
        />
      </AdminFormRow>
    </AdminFormSection>
  );
});
